import React from 'react';
import {
  BaseButton,
  useCopyToast,
  formatDateToShortDotV2,
} from '@repo/common';

import { ReactComponent as LinksIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';

const InvoiceHeader = ({ data, $isMobile }) => {
  const totalAmount = data?.amounts?.reduce((sum, amt) => sum + amt, 0) ?? 0;
  const { copy, copyToast } = useCopyToast();

  // KRW(계좌이체) 인보이스는 "55,000 KRW", 그 외는 기존대로 "15.00 $"
  const isKrw = String(data?.currency || '').toUpperCase() === 'KRW';
  const formattedAmount = isKrw
    ? `${Math.round(totalAmount).toLocaleString('en-US')} KRW`
    : `${totalAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} $`;

  const getPaymentStatus = (statuses = []) => {
    if (!statuses.length) return 'UNPAID';

    const paidSet = ['CONFIRMED', 'ATTENDED', 'ABSENT'];

    const allCanceled = statuses.every((s) => s === 'CANCELED');
    if (allCanceled) return 'CANCELED';
    // 환불 요청 시점에 예약이 REFUNDED 로 바뀌므로 "UNPAID"(미결제)로 보이면 안 된다
    if (statuses.every((s) => s === 'REFUNDED' || s === 'CANCELED')) {
      return 'REFUNDED';
    }
    if (statuses.every((s) => s === 'EXPIRED')) return 'EXPIRED';

    const paidCount = statuses.filter((s) => paidSet.includes(s)).length;

    if (paidCount === statuses.length) return 'FULLY PAID';
    if (paidCount > 0) return 'PARTIALLY PAID';

    return 'UNPAID';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'FULLY PAID':
        return 'bg-green-light02 text-green-deep04';

      case 'PARTIALLY PAID':
      case 'REFUNDED':
        return 'bg-purple-purple01 text-purple-purple05';

      case 'CANCELED':
      case 'EXPIRED':
        return 'bg-grayscale-gray02 text-grayscale-gray05';

      case 'UNPAID':
        return 'bg-functional-failed01 text-functional-failed03';

      default:
        return 'bg-green-light02 text-green-deep04';
    }
  };

  const paymentStatus = getPaymentStatus(data?.statuses);

  const handleCopyLink = () => copy(window.location.href);

  if ($isMobile) {
    return (
      <div className='flex flex-col items-center gap-2 w-full'>
        <BaseButton
          hideIcon
          isStatic
          type='text'
          size='s'
          bgclass={`px-2 py-0.5 ${getStatusStyle(paymentStatus)} typo-b11Strong h-6`}
        >
          {paymentStatus}
        </BaseButton>

        <div className='typo-h6 text-text-primary'>{formattedAmount}</div>

        <div className='typo-b13 text-text-tertiary text-center'>
          {data?.invoiceNumber} · Issued{' '}
          {data?.createdAt ? formatDateToShortDotV2(data.createdAt) : '-'}
        </div>

        {copyToast}
      </div>
    );
  }

  return (
    <div className='flex justify-between items-start w-full'>
      <div className='flex flex-col items-start gap-1'>
        {!$isMobile && (
          <div className='typo-b18Strong text-text-primary'>Invoice</div>
        )}
        <div className='flex items-center gap-2'>
          <div className='typo-h7 text-text-success'>{data?.invoiceNumber}</div>
          <div className='typo-title3 text-text-placeholder'>for</div>
          <div className='typo-h7 text-text-primary'>{formattedAmount}</div>
          <BaseButton
            hideIcon
            isStatic
            type='text'
            size='s'
            bgclass={`px-2 py-0.5 ${getStatusStyle(
              paymentStatus,
            )} typo-b12Strong h-6`}
          >
            {paymentStatus}
          </BaseButton>
        </div>
      </div>
      <button
        onClick={handleCopyLink}
        className='flex justify-center items-center h-[27px] gap-1 typo-b14 text-green-deep02'
      >
        <LinksIcon />
        Copy link
      </button>

      {copyToast}
    </div>
  );
};

export default InvoiceHeader;
